import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis } from 'recharts';
const ProcurementCostTrendsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Material Cost Index',
    value: '112.5',
    change: '+8.2%',
    isPositive: false,
    subtitle: 'Base 100 (Jan 2023)'
  }, {
    title: 'Monthly Purchase Spend',
    value: '₹485 Cr',
    change: '+12.5%',
    isPositive: false,
    subtitle: 'Current month'
  }];
  // Material Price Trend Data
  const materialPriceTrendData = [{
    month: 'Jan',
    steel: 100,
    aluminum: 100,
    plastic: 100,
    copper: 100,
    rubber: 100
  }, {
    month: 'Feb',
    steel: 102.5,
    aluminum: 103.2,
    plastic: 101.8,
    copper: 104.5,
    rubber: 102.2
  }, {
    month: 'Mar',
    steel: 105.2,
    aluminum: 106.8,
    plastic: 103.5,
    copper: 108.2,
    rubber: 103.8
  }, {
    month: 'Apr',
    steel: 107.8,
    aluminum: 109.5,
    plastic: 104.2,
    copper: 112.5,
    rubber: 105.2
  }, {
    month: 'May',
    steel: 109.5,
    aluminum: 112.8,
    plastic: 105.8,
    copper: 116.8,
    rubber: 106.5
  }, {
    month: 'Jun',
    steel: 111.2,
    aluminum: 115.5,
    plastic: 107.2,
    copper: 120.2,
    rubber: 107.8
  }, {
    month: 'Jul',
    steel: 112.8,
    aluminum: 117.2,
    plastic: 108.5,
    copper: 122.8,
    rubber: 108.8
  }, {
    month: 'Aug',
    steel: 113.5,
    aluminum: 118.5,
    plastic: 109.2,
    copper: 124.5,
    rubber: 109.5
  }, {
    month: 'Sep',
    steel: 112.5,
    aluminum: 117.8,
    plastic: 108.8,
    copper: 123.2,
    rubber: 108.9
  }];
  // Purchase Spend Trend Data
  const purchaseSpendTrendData = [{
    month: 'Jan',
    rawMaterials: 245,
    components: 125,
    subAssemblies: 85,
    total: 455
  }, {
    month: 'Feb',
    rawMaterials: 252,
    components: 128,
    subAssemblies: 87,
    total: 467
  }, {
    month: 'Mar',
    rawMaterials: 258,
    components: 132,
    subAssemblies: 89,
    total: 479
  }, {
    month: 'Apr',
    rawMaterials: 248,
    components: 126,
    subAssemblies: 84,
    total: 458
  }, {
    month: 'May',
    rawMaterials: 262,
    components: 134,
    subAssemblies: 91,
    total: 487
  }, {
    month: 'Jun',
    rawMaterials: 268,
    components: 138,
    subAssemblies: 93,
    total: 499
  }, {
    month: 'Jul',
    rawMaterials: 265,
    components: 136,
    subAssemblies: 92,
    total: 493
  }, {
    month: 'Aug',
    rawMaterials: 272,
    components: 140,
    subAssemblies: 95,
    total: 507
  }, {
    month: 'Sep',
    rawMaterials: 275,
    components: 142,
    subAssemblies: 96,
    total: 513
  }];
  // Commodity Correlation Data
  const commodityCorrelationData = [{
    material1: 'Steel',
    material2: 'Aluminum',
    correlation: 0.85,
    priceChange1: 12.5,
    priceChange2: 17.8,
    volume: 450
  }, {
    material1: 'Steel',
    material2: 'Copper',
    correlation: 0.72,
    priceChange1: 12.5,
    priceChange2: 23.2,
    volume: 380
  }, {
    material1: 'Aluminum',
    material2: 'Copper',
    correlation: 0.78,
    priceChange1: 17.8,
    priceChange2: 23.2,
    volume: 320
  }, {
    material1: 'Steel',
    material2: 'Plastic',
    correlation: 0.45,
    priceChange1: 12.5,
    priceChange2: 8.8,
    volume: 420
  }, {
    material1: 'Plastic',
    material2: 'Rubber',
    correlation: 0.68,
    priceChange1: 8.8,
    priceChange2: 8.9,
    volume: 280
  }, {
    material1: 'Aluminum',
    material2: 'Plastic',
    correlation: 0.52,
    priceChange1: 17.8,
    priceChange2: 8.8,
    volume: 350
  }];
  // Vendor-wise Cost Trends Table Data
  const vendorCostTrendsData = [{
    vendor: 'Bharat Forge',
    category: 'Steel Forgings',
    basePrice: 142,
    currentPrice: 162,
    priceChange: 20,
    priceChangePercent: 14.1,
    q1Spend: 68,
    q2Spend: 72,
    q3Spend: 75,
    totalSpend: 215,
    volumeChange: 2.5,
    costDriver: 'Steel price increase',
    trend: 'up'
  }, {
    vendor: 'Motherson Sumi',
    category: 'Wiring Harness',
    basePrice: 285,
    currentPrice: 312,
    priceChange: 27,
    priceChangePercent: 9.5,
    q1Spend: 102,
    q2Spend: 108,
    q3Spend: 112,
    totalSpend: 322,
    volumeChange: 3.8,
    costDriver: 'Copper price surge',
    trend: 'up'
  }, {
    vendor: 'Amara Raja',
    category: 'Batteries',
    basePrice: 425,
    currentPrice: 445,
    priceChange: 20,
    priceChangePercent: 4.7,
    q1Spend: 45,
    q2Spend: 48,
    q3Spend: 50,
    totalSpend: 143,
    volumeChange: 1.2,
    costDriver: 'Lead price stable',
    trend: 'stable'
  }, {
    vendor: 'Minda Industries',
    category: 'Switches',
    basePrice: 185,
    currentPrice: 195,
    priceChange: 10,
    priceChangePercent: 5.4,
    q1Spend: 38,
    q2Spend: 42,
    q3Spend: 44,
    totalSpend: 124,
    volumeChange: 4.5,
    costDriver: 'Plastic cost up',
    trend: 'up'
  }, {
    vendor: 'Samvardhana',
    category: 'Interior Trim',
    basePrice: 225,
    currentPrice: 238,
    priceChange: 13,
    priceChangePercent: 5.8,
    q1Spend: 32,
    q2Spend: 35,
    q3Spend: 38,
    totalSpend: 105,
    volumeChange: 2.8,
    costDriver: 'Fabric prices up',
    trend: 'up'
  }, {
    vendor: 'Varroc Engineering',
    category: 'Lighting',
    basePrice: 385,
    currentPrice: 368,
    priceChange: -17,
    priceChangePercent: -4.4,
    q1Spend: 48,
    q2Spend: 52,
    q3Spend: 54,
    totalSpend: 154,
    volumeChange: 5.2,
    costDriver: 'LED cost reduction',
    trend: 'down'
  }, {
    vendor: 'Gabriel India',
    category: 'Shock Absorbers',
    basePrice: 685,
    currentPrice: 728,
    priceChange: 43,
    priceChangePercent: 6.3,
    q1Spend: 28,
    q2Spend: 32,
    q3Spend: 35,
    totalSpend: 95,
    volumeChange: 3.2,
    costDriver: 'Steel & oil prices',
    trend: 'up'
  }, {
    vendor: 'Sona BLW',
    category: 'Gears',
    basePrice: 545,
    currentPrice: 598,
    priceChange: 53,
    priceChangePercent: 9.7,
    q1Spend: 52,
    q2Spend: 58,
    q3Spend: 62,
    totalSpend: 172,
    volumeChange: 2.8,
    costDriver: 'Alloy steel costs',
    trend: 'up'
  }, {
    vendor: 'Endurance Tech',
    category: 'Suspension',
    basePrice: 425,
    currentPrice: 455,
    priceChange: 30,
    priceChangePercent: 7.1,
    q1Spend: 42,
    q2Spend: 45,
    q3Spend: 48,
    totalSpend: 135,
    volumeChange: 1.8,
    costDriver: 'Raw material mix',
    trend: 'up'
  }, {
    vendor: 'Lumax Industries',
    category: 'Lighting',
    basePrice: 295,
    currentPrice: 285,
    priceChange: -10,
    priceChangePercent: -3.4,
    q1Spend: 32,
    q2Spend: 35,
    q3Spend: 38,
    totalSpend: 105,
    volumeChange: 4.8,
    costDriver: 'Component sourcing',
    trend: 'down'
  }];
  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUpIcon size={14} className="text-red-600" data-id="element-9796" />;
    if (trend === 'down') return <TrendingDownIcon size={14} className="text-green-600" data-id="element-9797" />;
    return <span className="text-gray-400" data-id="element-9798">—</span>;
  };
  const getCorrelationColor = (correlation: number) => {
    if (correlation >= 0.7) return '#ef4444';
    if (correlation >= 0.5) return '#f59e0b';
    return '#10b981';
  };
  return <div data-id="element-9799">
      <DescriptionPanel title="Procurement Cost Trends & Analysis" description="Track material price movements, purchase spend patterns, and commodity correlations to optimize procurement strategy. Monitor vendor-wise cost trends and identify cost drivers to manage procurement expenses effectively." insights={['Material Cost Index at 112.5 - up 8.2% from baseline driven by steel and copper price increases', 'Monthly purchase spend at ₹485 Cr - up 12.5% YoY with volume growth contributing 3.2%', 'Copper prices showing highest increase at 23.2% - impacting wiring harness costs significantly', 'Strong correlation (0.85) between steel and aluminum prices - coordinated hedging recommended']} data-id="element-9800" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-9801">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9802">
          Procurement Cost Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9803">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9804">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9805">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-9806">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9807">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9808">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-9809" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-9810" />}
                {kpi.change} vs baseline
              </div>
            </Card>)}
        </div>
      </div>
      {/* Material Price Trend and Purchase Spend Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-9811">
        {/* Material Price Trend */}
        <Card className="p-6" data-id="element-9812">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9813">
            Material Price Trend Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9814">
            Price index by material type (Base 100 = Jan 2023)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9815">
            <LineChart data={materialPriceTrendData} data-id="element-9816">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9817" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9818" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[95, 130]} data-id="element-9819" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => value.toFixed(1)} data-id="element-9820" />
              <Legend data-id="element-9821" />
              <Line type="monotone" dataKey="steel" stroke="#6b7280" strokeWidth={3} name="Steel" dot={{
              r: 4
            }} data-id="element-9822" />
              <Line type="monotone" dataKey="aluminum" stroke="#3b82f6" strokeWidth={3} name="Aluminum" dot={{
              r: 4
            }} data-id="element-9823" />
              <Line type="monotone" dataKey="plastic" stroke="#10b981" strokeWidth={3} name="Plastic" dot={{
              r: 4
            }} data-id="element-9824" />
              <Line type="monotone" dataKey="copper" stroke="#ef4444" strokeWidth={3} name="Copper" dot={{
              r: 4
            }} data-id="element-9825" />
              <Line type="monotone" dataKey="rubber" stroke="#f59e0b" strokeWidth={3} name="Rubber" dot={{
              r: 4
            }} data-id="element-9826" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9827">
            <div className="grid grid-cols-5 gap-2 text-sm" data-id="element-9828">
              <div className="text-center" data-id="element-9829">
                <div className="text-gray-600" data-id="element-9830">Steel</div>
                <div className="text-lg font-bold text-gray-700" data-id="element-9831">112.5</div>
                <div className="text-xs text-red-600" data-id="element-9832">+12.5%</div>
              </div>
              <div className="text-center" data-id="element-9833">
                <div className="text-gray-600" data-id="element-9834">Aluminum</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-9835">117.8</div>
                <div className="text-xs text-red-600" data-id="element-9836">+17.8%</div>
              </div>
              <div className="text-center" data-id="element-9837">
                <div className="text-gray-600" data-id="element-9838">Plastic</div>
                <div className="text-lg font-bold text-green-600" data-id="element-9839">108.8</div>
                <div className="text-xs text-red-600" data-id="element-9840">+8.8%</div>
              </div>
              <div className="text-center" data-id="element-9841">
                <div className="text-gray-600" data-id="element-9842">Copper</div>
                <div className="text-lg font-bold text-red-600" data-id="element-9843">123.2</div>
                <div className="text-xs text-red-600" data-id="element-9844">+23.2%</div>
              </div>
              <div className="text-center" data-id="element-9845">
                <div className="text-gray-600" data-id="element-9846">Rubber</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-9847">108.9</div>
                <div className="text-xs text-red-600" data-id="element-9848">+8.9%</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Purchase Spend Trend */}
        <Card className="p-6" data-id="element-9849">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9850">
            Purchase Spend Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9851">
            Monthly procurement spend by category (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9852">
            <BarChart data={purchaseSpendTrendData} data-id="element-9853">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9854" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9855" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-9856" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-9857" />
              <Legend data-id="element-9858" />
              <Bar dataKey="rawMaterials" stackId="a" fill="#3b82f6" name="Raw Materials" data-id="element-9859" />
              <Bar dataKey="components" stackId="a" fill="#10b981" name="Components" data-id="element-9860" />
              <Bar dataKey="subAssemblies" stackId="a" fill="#f59e0b" name="Sub-Assemblies" data-id="element-9861" />
              <Line type="monotone" dataKey="total" stroke="#ef4444" strokeWidth={3} name="Total" dot={{
              r: 5
            }} data-id="element-9862" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9863">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-9864">
              <div className="text-center" data-id="element-9865">
                <div className="text-gray-600" data-id="element-9866">Raw Materials</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-9867">₹275 Cr</div>
                <div className="text-xs text-gray-500" data-id="element-9868">53.6% of total</div>
              </div>
              <div className="text-center" data-id="element-9869">
                <div className="text-gray-600" data-id="element-9870">Components</div>
                <div className="text-lg font-bold text-green-600" data-id="element-9871">₹142 Cr</div>
                <div className="text-xs text-gray-500" data-id="element-9872">27.7% of total</div>
              </div>
              <div className="text-center" data-id="element-9873">
                <div className="text-gray-600" data-id="element-9874">Sub-Assemblies</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-9875">₹96 Cr</div>
                <div className="text-xs text-gray-500" data-id="element-9876">18.7% of total</div>
              </div>
              <div className="text-center" data-id="element-9877">
                <div className="text-gray-600" data-id="element-9878">Total Spend</div>
                <div className="text-lg font-bold text-red-600" data-id="element-9879">₹513 Cr</div>
                <div className="text-xs text-red-600" data-id="element-9880">+12.7% MoM</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Commodity Correlation Chart */}
      <Card className="p-6 mb-8" data-id="element-9881">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9882">
          Commodity Correlation Chart
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-9883">
          Price correlation between material pairs (bubble size = procurement
          volume)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-9884">
          <ScatterChart data-id="element-9885">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9886" />
            <XAxis type="number" dataKey="priceChange1" name="Material 1 Price Change" unit="%" tick={{
            fontSize: 11
          }} label={{
            value: 'Material 1 Price Change (%)',
            position: 'insideBottom',
            offset: -5,
            style: {
              fontSize: 11
            }
          }} domain={[0, 25]} data-id="element-9887" />
            <YAxis type="number" dataKey="priceChange2" name="Material 2 Price Change" unit="%" tick={{
            fontSize: 11
          }} label={{
            value: 'Material 2 Price Change (%)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 11
            }
          }} domain={[0, 25]} data-id="element-9888" />
            <ZAxis type="number" dataKey="volume" range={[100, 500]} data-id="element-9889" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} cursor={{
            strokeDasharray: '3 3'
          }} content={({
            active,
            payload
          }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm" data-id="element-9891">
                      <p className="font-semibold text-gray-800 mb-1" data-id="element-9892">
                        {data.material1} vs {data.material2}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-9893">
                        Correlation: {data.correlation.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-9894">
                        {data.material1}: +{data.priceChange1}%
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-9895">
                        {data.material2}: +{data.priceChange2}%
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-9896">
                        Volume: ₹{data.volume} Cr
                      </p>
                    </div>;
            }
            return null;
          }} data-id="element-9890" />
            {commodityCorrelationData.map((entry, index) => <Scatter key={index} data={[entry]} fill={getCorrelationColor(entry.correlation)} opacity={0.7} data-id="element-9897" />)}
          </ScatterChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9898">
          <div className="flex items-center justify-between text-sm" data-id="element-9899">
            <div className="flex items-center space-x-4" data-id="element-9900">
              <div className="flex items-center" data-id="element-9901">
                <div className="w-4 h-4 rounded-full bg-red-500 mr-2" data-id="element-9902" />
                <span className="text-gray-600" data-id="element-9903">High Correlation (≥0.7)</span>
              </div>
              <div className="flex items-center" data-id="element-9904">
                <div className="w-4 h-4 rounded-full bg-orange-500 mr-2" data-id="element-9905" />
                <span className="text-gray-600" data-id="element-9906">
                  Medium Correlation (0.5-0.7)
                </span>
              </div>
              <div className="flex items-center" data-id="element-9907">
                <div className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-9908" />
                <span className="text-gray-600" data-id="element-9909">Low Correlation (&lt;0.5)</span>
              </div>
            </div>
            <span className="text-gray-500" data-id="element-9910">
              Bubble size = Procurement volume (₹ Cr)
            </span>
          </div>
        </div>
      </Card>
      {/* Vendor-wise Cost Trends Table */}
      <Card className="p-6" data-id="element-9911">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9912">
          Vendor-wise Cost Trends Analysis
        </h3>
        <div className="overflow-x-auto" data-id="element-9913">
          <table className="w-full" data-id="element-9914">
            <thead data-id="element-9915">
              <tr className="border-b-2 border-gray-300" data-id="element-9916">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9917">
                  Vendor
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9918">
                  Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9919">
                  Base Price
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9920">
                  Current Price
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9921">
                  Price Change
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9922">
                  Change %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9923">
                  Q1 Spend
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9924">
                  Q2 Spend
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9925">
                  Q3 Spend
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9926">
                  Total Spend
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9927">
                  Volume Δ
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9928">
                  Cost Driver
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9929">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9930">
              {vendorCostTrendsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9931">
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-9932">
                    {row.vendor}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9933">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-600" data-id="element-9934">
                    ₹{row.basePrice}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-9935">
                    ₹{row.currentPrice}
                  </td>
                  <td className={`py-3 px-4 text-sm text-right font-semibold ${row.priceChange > 0 ? 'text-red-600' : 'text-green-600'}`} data-id="element-9936">
                    {row.priceChange > 0 ? '+' : ''}₹{row.priceChange}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9937">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.priceChangePercent > 10 ? 'bg-red-100 text-red-700' : row.priceChangePercent > 5 ? 'bg-orange-100 text-orange-700' : row.priceChangePercent > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`} data-id="element-9938">
                      {row.priceChangePercent > 0 ? '+' : ''}
                      {row.priceChangePercent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9939">
                    ₹{row.q1Spend} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9940">
                    ₹{row.q2Spend} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9941">
                    ₹{row.q3Spend} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-9942">
                    ₹{row.totalSpend} Cr
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-medium text-green-600" data-id="element-9943">
                    +{row.volumeChange}%
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600" data-id="element-9944">
                    {row.costDriver}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9945">
                    {getTrendIcon(row.trend)}
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-9946">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-9947">AVERAGE</td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9948">-</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-9949">
                  ₹360
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-9950">
                  ₹379
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-9951">
                  +₹19
                </td>
                <td className="py-3 px-4 text-center" data-id="element-9952">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700" data-id="element-9953">
                    +5.8%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-9954">
                  ₹49 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-9955">
                  ₹53 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-9956">
                  ₹55 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-9957">
                  ₹157 Cr
                </td>
                <td className="py-3 px-4 text-center text-sm text-green-600" data-id="element-9958">
                  +3.2%
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9959">-</td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9960">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9961">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-9962">
            <div className="text-center" data-id="element-9963">
              <div className="text-gray-600" data-id="element-9964">Total Vendors</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9965">10</div>
            </div>
            <div className="text-center" data-id="element-9966">
              <div className="text-gray-600" data-id="element-9967">Avg Price Increase</div>
              <div className="text-xl font-bold text-red-600" data-id="element-9968">+5.8%</div>
            </div>
            <div className="text-center" data-id="element-9969">
              <div className="text-gray-600" data-id="element-9970">Total Q3 Spend</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-9971">₹555 Cr</div>
            </div>
            <div className="text-center" data-id="element-9972">
              <div className="text-gray-600" data-id="element-9973">YTD Spend</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-9974">₹1,570 Cr</div>
            </div>
            <div className="text-center" data-id="element-9975">
              <div className="text-gray-600" data-id="element-9976">Avg Volume Growth</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9977">+3.2%</div>
            </div>
            <div className="text-center" data-id="element-9978">
              <div className="text-gray-600" data-id="element-9979">Price Declining</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9980">2/10</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ProcurementCostTrendsDashboard;